import { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { mediaUrl } from "@/lib/api";
import { prod, errMsg, fmtDate } from "@/lib/prodApi";

const n = (x) => (x === null || x === undefined ? "—" : x.toLocaleString("en-GB"));

function Credits({ c }) {
  if (!c) return <p className="warning-text mb-8" data-testid="credits-unavailable"><span className="warning-icon">!</span><strong>ElevenLabs credits unavailable right now.</strong></p>;
  return (
    <dl className="grid grid-cols-3 gap-6 mb-8" data-testid="elevenlabs-credits">
      <div className="panel p-6"><dt className="text-[17px] text-[#505a5f]">Characters remaining this month</dt><dd className="text-4xl font-black text-[#0033A1]" data-testid="credits-remaining">{n(c.remaining)}</dd></div>
      <div className="panel p-6"><dt className="text-[17px] text-[#505a5f]">Used</dt><dd className="text-3xl font-black" data-testid="credits-used">{n(c.used)} / {n(c.limit)}</dd></div>
      <div className="panel p-6"><dt className="text-[17px] text-[#505a5f]">Resets</dt><dd className="text-2xl font-black" data-testid="credits-reset">{fmtDate(c.resets_at)}</dd></div>
    </dl>
  );
}

function Row({ r, onRegen }) {
  const running = r.job && r.job.status === "running";
  return (
    <li className="panel p-6" data-testid={`audio-${r.key}`}>
      <div className="flex items-start gap-6">
        <div className="flex-1">
          <h3 className="text-2xl font-black">{r.label}</h3>
          <p className="text-[17px] mt-1" data-testid={`audio-${r.key}-cast`}><strong>Voice cast:</strong> {r.cast.join(" · ") || "—"}</p>
          <p className="text-[16px] text-[#505a5f]" data-testid={`audio-${r.key}-date`}>Generated {fmtDate(r.generated_at)}{r.duration_sec ? ` · ${Math.floor(r.duration_sec / 60)}:${String(Math.round(r.duration_sec % 60)).padStart(2, "0")}` : ""}{r.model ? ` · ${r.model}` : ""}</p>
          {r.next_cast.join() !== r.cast.join() && <p className="text-[16px] text-[#505a5f]" data-testid={`audio-${r.key}-next-cast`}>Next regeneration: {r.next_cast.join(" · ")}</p>}
          {running && <p className="text-[17px] font-bold text-[#0033A1] mt-2" data-testid={`audio-${r.key}-running`}>Regenerating… this can take a few minutes. The current recording stays live until the new one is ready.</p>}
          {r.job && r.job.status === "failed" && <p className="form-error error-msg mt-2 text-[16px]" data-testid={`audio-${r.key}-error`}>{r.job.error}</p>}
        </div>
        <div className="flex flex-col gap-3 items-end">
          <a className="btn-ghost !py-2 !text-[17px]" href={mediaUrl(`/api/production/audio/${r.key}/file?download=1`)} download data-testid={`audio-${r.key}-download`}><Download size={17} /> Download MP3</a>
          <button className="btn-ghost !py-2 !text-[17px]" disabled={running} onClick={() => onRegen(r)} data-testid={`audio-${r.key}-regenerate`}><RefreshCw size={17} /> Regenerate</button>
        </div>
      </div>
      {r.available && <audio controls preload="none" src={mediaUrl(r.url)} className="w-full mt-4" data-testid={`audio-${r.key}-player`} />}
    </li>
  );
}

export default function AudioLibrary() {
  const [data, setData] = useState(null);
  const [est, setEst] = useState(null);
  const reload = useCallback(() => prod.get("/audio").then((r) => setData(r.data)), []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (!data || !data.recordings.some((r) => r.job && r.job.status === "running")) return undefined;
    const t = setInterval(reload, 6000);
    return () => clearInterval(t);
  }, [data, reload]);

  const askRegen = async (r) => {
    try {
      const { data: e } = await prod.get(`/audio/${r.key}/estimate`);
      setEst({ ...e, label: r.label });
    } catch (e) {
      toast.error(errMsg(e, "Could not estimate the cost"));
    }
  };
  const confirmRegen = async () => {
    const e = est;
    setEst(null);
    try {
      await prod.post(`/audio/${e.key}/regenerate`);
      toast.success(`${e.label}: regenerating`);
    } catch (err) {
      toast.error(errMsg(err, "Regeneration failed. The existing recording has been kept."));
    }
    reload();
  };

  if (!data) return <p className="text-lg py-8">Loading audio library…</p>;
  return (
    <div data-testid="audio-section">
      <div className="flex items-end gap-4 mb-8">
        <div className="flex-1"><h2 className="text-4xl font-black">Audio</h2><p className="text-lg text-[#505a5f] mt-2">The six recordings used in the game.</p></div>
        <a className="btn-spore" href={mediaUrl("/api/production/audio/all.zip")} download data-testid="download-all-audio"><Download size={18} /> Download all audio</a>
      </div>
      <Credits c={data.credits} />
      <ul className="space-y-6">{data.recordings.map((r) => <Row key={r.key} r={r} onRegen={askRegen} />)}</ul>
      <AlertDialog open={!!est} onOpenChange={(o) => !o && setEst(null)}>
        <AlertDialogContent className="rounded-none border-2 border-[#0b0c0c] max-w-xl" data-testid="regenerate-confirm-dialog">
          {est && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-3xl font-black">Regenerate {est.label}?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="text-lg text-[#0b0c0c] space-y-3">
                    <p data-testid="regenerate-estimate">Estimated cost: <strong>{n(est.characters)} characters</strong>. You have {n(est.remaining)} left (we keep a {est.safety_margin}-character safety margin).</p>
                    {!est.sufficient && <p className="error-msg" data-testid="regenerate-insufficient">Not enough ElevenLabs characters for this recording. The existing recording will be kept.</p>}
                    <p>Nothing is generated until you confirm. The current recording stays live if anything fails.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-3">
                <AlertDialogCancel className="btn-ghost h-auto" data-testid="regenerate-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction className="btn-spore h-auto" disabled={!est.sufficient} onClick={confirmRegen} data-testid="regenerate-confirm">Regenerate</AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
